import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddDogsModal, HuntDogsSection, useToast, type HuntDog } from '@wehunt/ui'
import { useMyDogs, type MyDog } from './useMyDogs'
import { useRegisterPlanDogs, type PlanDogHeaderWire } from './useRegisterPlanDogs'

/** Mobile's default dog colour, stamped on a header when a dog has none. */
const DEFAULT_DOG_ARGB = 0xfffa6400

function toHeader(dog: MyDog): PlanDogHeaderWire {
  return {
    id: dog.id,
    name: dog.name,
    ownerName: dog.ownerName,
    // `>>> 0` writes the colour as the UNSIGNED 32-bit value mobile writes: the
    // API read wraps a full-alpha colour to a negative int, which must not reach
    // the shared header the start copy and mobile both read.
    dogColor: (dog.colorArgb ?? DEFAULT_DOG_ARGB) >>> 0,
    dogIcon: dog.icon ?? '',
    devices: dog.devices,
    // Preserve the dog's own visibility — mobile always writes it, and an absent
    // value defaults to public on the started hunt, so dropping it would turn a
    // private dog public.
    visibility: dog.visibility ?? undefined,
  }
}

/**
 * The planned-hunt Dogs section container (CLUB-883). Shows the plan's
 * registered dogs (from the detail read) and lets the signed-in user add or
 * remove their own dogs — a leader may remove any dog — through the
 * `registerplandogs` Cloud Function. The picker lists only the user's OWN dogs;
 * every rule is enforced server-side, so the remove control here is affordance
 * only. On failure it surfaces a non-blocking toast; the mutation invalidates
 * the detail query, so the section refreshes itself.
 */
export function HuntPlanDogs({
  groupId,
  huntPlanId,
  registered,
  isOrganizer,
}: {
  groupId: string
  huntPlanId: string
  registered: HuntDog[]
  isOrganizer: boolean
}) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const { data: myDogs } = useMyDogs()
  const register = useRegisterPlanDogs(groupId, huntPlanId)
  const [picking, setPicking] = useState(false)

  // Undefined until the own-dogs read resolves. The Add/Edit button is never
  // disabled for this — it is the section's only entry point. Instead the picker
  // is re-keyed on it (below): the modal seeds its selection ONCE from the
  // registered set at mount, so opening it before the dogs load and then seeing
  // them arrive would otherwise leave a frozen-empty selection whose Done
  // unregisters every dog; the key change remounts it with the real selection.
  const dogsLoading = myDogs === undefined
  const own = myDogs ?? []
  const ownById = new Map(own.map((d) => [d.id, d]))
  const registeredIds = new Set(registered.map((d) => d.id))
  const initiallySelectedIds = own.filter((d) => registeredIds.has(d.id)).map((d) => d.id)

  const commit = async (add: PlanDogHeaderWire[], remove: string[]) => {
    if (add.length === 0 && remove.length === 0) return
    try {
      await register.mutateAsync({ add, remove })
    } catch {
      // The callable's own wording is server phrasing (see useRegisterPlanDogs);
      // show our own localized message instead.
      showToast(t('routes.huntDetail.dogsError'))
    }
  }

  return (
    <>
      <HuntDogsSection
        dogs={registered}
        busy={register.isPending}
        onAddEdit={() => setPicking(true)}
        // A dog is removable when it is the viewer's own, or when the viewer
        // leads the plan. The remove control sends only that dog's id — a header
        // is not needed to unregister, and a leader removing another member's
        // dog has no header for it anyway.
        canRemove={(dog) => isOrganizer || ownById.has(dog.id)}
        onRemove={(dogId) => void commit([], [dogId])}
      />
      {picking && (
        <AddDogsModal
          // Remount when the own-dogs read flips from loading to ready, so the
          // selection is seeded from the real registered set rather than the
          // empty one a pre-load open would have frozen.
          key={dogsLoading ? 'loading' : 'ready'}
          dogs={own}
          initiallySelectedIds={initiallySelectedIds}
          saving={register.isPending}
          onClose={() => setPicking(false)}
          onDone={(selectedIds) => {
            const selected = new Set(selectedIds)
            const add = own
              .filter((d) => selected.has(d.id) && !registeredIds.has(d.id))
              .map(toHeader)
            // Only the user's own dogs can be toggled off in the picker; a
            // leader removes another member's dog from the row control instead.
            const remove = own
              .filter((d) => !selected.has(d.id) && registeredIds.has(d.id))
              .map((d) => d.id)
            setPicking(false)
            void commit(add, remove)
          }}
        />
      )}
    </>
  )
}
